import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PublicFooter from './PublicFooter';
import AboutBookModal from './AboutBookModal';
import RegistrationModal from './RegistrationModal';
import { Search, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BOOK_TYPE_OPTIONS } from '../constants/genreThemes';
import { BACKEND_URL } from '@/config/api';
import { Seo, PAGE_SEO } from './Seo';

// BACKEND_URL imported from centralized config

// Helper function to capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};


// SEO-1 F3: real <a href="/book/{slug}"> for crawlers; a plain click is left to the card's onClick (opens the modal).
const CrawlableBookLink = ({ book, testId, children }) => (
  book.seo_slug ? (
    <a href={`/book/${book.seo_slug}`} onClick={(e) => e.preventDefault()} className="block" data-testid={testId}>
      {children}
    </a>
  ) : children
);

const PublicLibraryPage = () => {
  const navigate = useNavigate();
  
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('title-asc');
  const [selectedBookType, setSelectedBookType] = useState('all');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [jumpToAuthor, setJumpToAuthor] = useState('');

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    setShowRegistrationModal(true);
  };

  // Fetch all available manuscripts
  useEffect(() => {
    fetchManuscripts();
  }, []);

  const fetchManuscripts = async () => {
    try {
      setLoading(true);
      
      // Use /api/library/public to only get activated books
      const response = await fetch(`${BACKEND_URL}/api/library/public`);

      if (!response.ok) {
        throw new Error('Failed to fetch catalog');
      }

      const data = await response.json();
      setManuscripts(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Extract unique genres and themes for dropdowns
  // Filter out book types (Fiction, Non-Fiction, etc.) from genres since they are not actual genres
  const BOOK_TYPES_TO_EXCLUDE = ['fiction', 'non-fiction', 'textbook', 'instruction-manual', 'published-paper'];
  
  const genres = useMemo(() => {
    const genreSet = new Set(
      manuscripts
        .map(m => m.genre)
        .filter(g => g && !BOOK_TYPES_TO_EXCLUDE.includes(g.toLowerCase()))
    );
    return ['all', ...Array.from(genreSet).sort()];
  }, [manuscripts]);

  const themes = useMemo(() => {
    const themeSet = new Set(manuscripts.map(m => m.theme).filter(Boolean));
    return ['all', ...Array.from(themeSet).sort()];
  }, [manuscripts]);

  const authors = useMemo(() => {
    const authorSet = new Set(manuscripts.map(m => m.author).filter(Boolean));
    return ['', ...Array.from(authorSet).sort()];
  }, [manuscripts]);

  // Filter and sort manuscripts
  const filteredManuscripts = useMemo(() => {
    let result = [...manuscripts];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(book =>
        book.title?.toLowerCase().includes(search) ||
        book.author?.toLowerCase().includes(search)
      );
    }

    // Book Type filter
    if (selectedBookType !== 'all') {
      result = result.filter(book => book.book_type === selectedBookType);
    }

    // Genre filter
    if (selectedGenre !== 'all') {
      result = result.filter(book => book.genre === selectedGenre);
    }

    // Theme filter
    if (selectedTheme !== 'all') {
      result = result.filter(book => book.theme === selectedTheme);
    }

    // Jump to author
    if (jumpToAuthor) {
      result = result.filter(book => book.author === jumpToAuthor);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortOrder) {
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'author-asc':
          return (a.author || '').localeCompare(b.author || '');
        case 'author-desc':
          return (b.author || '').localeCompare(a.author || '');
        default:
          return 0;
      }
    });

    return result;
  }, [manuscripts, searchTerm, selectedBookType, selectedGenre, selectedTheme, jumpToAuthor, sortOrder]);

  // Reset all filters
  const handleShowAllTitles = () => {
    setSearchTerm('');
    setSelectedBookType('all');
    setSelectedGenre('all');
    setSelectedTheme('all');
    setJumpToAuthor('');
    setSortOrder('title-asc');
  };

  // Handle book click - show modal
  const handleBookClick = (book) => {
    setSelectedBook(book);
  };

  // Handle read now - open registration modal
  const handleReadNow = (book) => {
    setShowRegistrationModal(true);
  };

  // Handle add to library - open registration modal
  const handleAddToLibrary = (book) => {
    setShowRegistrationModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 overflow-x-hidden">
      <Seo {...PAGE_SEO.library} />
      {/* Header with Logo and Sign In */}
      <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="mx-auto max-w-6xl px-4">
          {/* Top Row: Logo and Sign In */}
          <div className="flex items-center justify-between py-3">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia™" 
                className="h-10 sm:h-12 w-auto rounded"
              />
            </Link>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={handleSignIn}
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-full px-4"
              >
                Sign In
              </Button>
              <Button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 rounded-full px-4 sm:px-5 font-semibold shadow-lg shadow-purple-900/30"
                data-testid="public-library-get-started-btn"
              >
                <span className="hidden sm:inline">Get Started Free</span>
                <span className="sm:hidden">Join Free</span>
              </Button>
            </div>
          </div>
          
          {/* Bottom Row: Navigation Menu */}
          <nav className="hidden md:flex items-center justify-center gap-8 py-2 border-t border-slate-800/50">
            <Link 
              to="/"
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              Welcome
            </Link>
            <Link 
              to="/about"
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              About
            </Link>
            <Link
              to="/pricing"
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              Pricing
            </Link>
            <Link 
              to="/faq"
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              FAQ
            </Link>
            <Link 
              to="/contact"
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              Contact
            </Link>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-500">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            NewAudia™ Public Library
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-sm sm:text-base text-purple-100/90">
            Discover NFD-SmartBooks<sup className="text-[0.6em]">™</sup> designed for listening, learning, and deep engagement. Browse our growing catalog, tap any book to learn more, and—soon—you'll be able to build your own personal NewAudia™ library.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8">
        {/* Filter Controls */}
        <div className="rounded-2xl bg-slate-900/80 p-6 ring-1 ring-slate-800 mb-8">
          {/* First Row: Search + All Titles + Title Sort + Select Author */}
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search with All Titles button */}
            <div className="relative flex gap-2 lg:col-span-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by title or author..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    // Reset other filters when searching
                    if (e.target.value) {
                      setSelectedBookType('all');
                      setSelectedGenre('all');
                      setSelectedTheme('all');
                      setJumpToAuthor('');
                    }
                  }}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 py-2.5 pl-10 pr-4 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleShowAllTitles}
                className="rounded-lg bg-slate-700 border border-slate-600 py-2.5 px-3 text-xs font-medium text-white hover:bg-slate-600 transition-colors whitespace-nowrap"
                title="Show all titles"
              >
                All Titles
              </button>
            </div>

            {/* Title Sort */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-desc">Title (Z-A)</option>
              <option value="author-asc">Author (A-Z)</option>
              <option value="author-desc">Author (Z-A)</option>
            </select>

            {/* Select Author */}
            <select
              value={jumpToAuthor}
              onChange={(e) => {
                const value = e.target.value;
                setJumpToAuthor(value);
                // Reset other filters when selecting author
                if (value) {
                  setSearchTerm('');
                  setSelectedBookType('all');
                  setSelectedGenre('all');
                  setSelectedTheme('all');
                }
              }}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select Author</option>
              {authors.filter(a => a).map(author => (
                <option key={author} value={author}>{capitalizeWords(author)}</option>
              ))}
            </select>

            {/* Select Book Type */}
            <select
              value={selectedBookType}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBookType(value);
                // Reset other filters when selecting book type
                if (value !== 'all') {
                  setSearchTerm('');
                  setSelectedGenre('all');
                  setSelectedTheme('all');
                  setJumpToAuthor('');
                }
              }}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Select Book Type</option>
              {BOOK_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Second Row: Genre + Theme */}
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 mt-4">
            {/* Select Genre */}
            <select
              value={selectedGenre}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedGenre(value);
                // Reset other filters when selecting genre
                if (value !== 'all') {
                  setSearchTerm('');
                  setSelectedBookType('all');
                  setSelectedTheme('all');
                  setJumpToAuthor('');
                }
              }}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Select Genre</option>
              {genres.filter(g => g !== 'all').map(genre => (
                <option key={genre} value={genre}>{capitalizeWords(genre)}</option>
              ))}
            </select>

            {/* Select Theme */}
            <select
              value={selectedTheme}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedTheme(value);
                // Reset other filters when selecting theme
                if (value !== 'all') {
                  setSearchTerm('');
                  setSelectedBookType('all');
                  setSelectedGenre('all');
                  setJumpToAuthor('');
                }
              }}
              className="rounded-lg bg-slate-800 border border-slate-700 py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Select Theme</option>
              {themes.filter(t => t !== 'all').map(theme => (
                <option key={theme} value={theme}>{capitalizeWords(theme)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        {!loading && !error && (
          <p className="text-sm text-slate-400 mb-6">
            Showing {filteredManuscripts.length} of {manuscripts.length} books
          </p>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid gap-6 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/3] rounded-lg bg-slate-800 mb-3"></div>
                <div className="h-4 w-3/4 rounded bg-slate-800 mb-1"></div>
                <div className="h-3 w-1/2 rounded bg-slate-800"></div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-2xl bg-red-900/20 p-8 ring-1 ring-red-800 text-center">
            <h3 className="text-lg font-semibold text-red-400">Error Loading Catalog</h3>
            <p className="mt-2 text-sm text-slate-300">{error}</p>
            <button
              onClick={fetchManuscripts}
              className="mt-4 rounded-full bg-red-600 px-6 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredManuscripts.length === 0 && (
          <div className="rounded-2xl bg-slate-900/60 p-12 text-center ring-1 ring-slate-800">
            <BookOpen className="mx-auto h-16 w-16 text-slate-600" />
            <h3 className="mt-6 text-xl font-semibold">
              {searchTerm || selectedBookType !== 'all' || selectedGenre !== 'all' || selectedTheme !== 'all' 
                ? 'No matching titles found' 
                : 'No titles available yet'}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              {searchTerm || selectedBookType !== 'all' || selectedGenre !== 'all' || selectedTheme !== 'all'
                ? 'Try adjusting your filters or search terms.'
                : 'Check back soon for new audiobook releases.'}
            </p>
            {(searchTerm || selectedBookType !== 'all' || selectedGenre !== 'all' || selectedTheme !== 'all') && (
              <button
                onClick={handleShowAllTitles}
                className="mt-4 rounded-full bg-purple-600 px-6 py-2 text-sm font-semibold text-white hover:bg-purple-500 transition-colors"
              >
                Show All Titles
              </button>
            )}
          </div>
        )}

        {/* Book Grid */}
        {!loading && !error && filteredManuscripts.length > 0 && (
          <div className="grid gap-4 grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
            {filteredManuscripts.map((book) => (
              <div
                key={book.id}
                className="group cursor-pointer max-w-[120px]"
                onClick={() => handleBookClick(book)}
                title={`Click to learn more about ${book.title}`}
              >
                <CrawlableBookLink book={book} testId={`library-book-cover-link-${book.id}`}>
                <div className="aspect-[2/3] w-full overflow-hidden rounded-md bg-slate-800 mb-2 ring-1 ring-slate-700 transition group-hover:ring-purple-500 group-hover:shadow-lg group-hover:shadow-purple-900/20">
                  {book.cover_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_url}`}
                      alt={book.title}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-500">
                      <BookOpen className="h-8 w-8" />
                    </div>
                  )}
                </div>
                </CrawlableBookLink>
                <h3 className="font-semibold text-slate-50 text-xs line-clamp-2 mb-0.5 group-hover:text-purple-400 transition">
                  <CrawlableBookLink book={book} testId={`library-book-title-link-${book.id}`}>{book.title}</CrawlableBookLink>
                </h3>
                <p className="text-[10px] text-slate-400 line-clamp-1">
                  {book.author}
                </p>
                {/* Sample Available badge - only shown for books with samples */}
                {book.hasSample && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 text-[8px] font-medium text-teal-300 bg-teal-900/40 rounded border border-teal-700/50">
                    Sample Available
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* About Book Modal */}
      <AboutBookModal
        book={selectedBook}
        isOpen={!!selectedBook}
        onClose={() => setSelectedBook(null)}
        isAuthenticated={false}
        onReadNow={handleReadNow}
        onAddToLibrary={handleAddToLibrary}
        onRegister={handleGetStarted}
      />

      {/* Sticky Conversion Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-purple-900/95 to-indigo-900/95 backdrop-blur-md border-t border-purple-500/30 py-3 px-4 md:hidden">
        <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">Get 10 free credits</p>
            <p className="text-xs text-purple-200 truncate">Start listening today</p>
          </div>
          <Button
            onClick={handleGetStarted}
            className="bg-white text-purple-700 hover:bg-purple-50 rounded-full px-4 py-2 text-sm font-semibold flex-shrink-0"
            data-testid="public-library-sticky-join-btn"
          >
            Join Free
          </Button>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />

      <PublicFooter />
    </div>
  );
};

export default PublicLibraryPage;
